// src/App.tsx (or AuthProvider.tsx)
import React, { ReactNode, useEffect, useState } from "react";
import { api } from "./api"; // axios instance
import { useAuthStore } from "../store/useAuthStore";
import { useTaskStore } from "../store/useTaskStore";
import { applyAuthSession } from "./authSession";

interface AuthBootstrapProps {
  children: ReactNode;
}

export function AuthBootstrap({ children }: AuthBootstrapProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Check if we have a JWT from the extension
    const urlParams = new URLSearchParams(window.location.search);
    const extJwt = urlParams.get('extJwt');

    if (extJwt) {
      // Extension provided JWT - verify it and log in
      console.log('🔐 Extension JWT detected, authenticating...');

      // Remove credentials from the address bar immediately so they do not
      // linger in browser history or get copied with the page URL.
      urlParams.delete('extJwt');
      const cleanUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', cleanUrl);

      api
        .get("/api/auth/me", {
          headers: { Authorization: `Bearer ${extJwt}` },
        })
        .then((res) => {
          const user = applyAuthSession(res.data);

          // Set default viewing user to logged-in user
          const taskStore = useTaskStore.getState();
          taskStore.setViewingUserId(user.user_id);
          taskStore.setViewScope('user');

          console.log('✅ Authenticated via extension JWT');

        })
        .catch((err) => {
          console.error('❌ Extension JWT authentication failed:', err);
        })
        .finally(() => setReady(true));
    } else {
      // Rehydrate the canonical user record as well as the persisted token.
      // Token refreshes and older stored sessions may contain only a partial
      // user object, which would otherwise make fields such as the avatar
      // disappear until the next full login.
      const storedJwt = localStorage.getItem("jwt") || useAuthStore.getState().token;
      if (!storedJwt) {
        setReady(true);
        return;
      }

      api
        .get("/api/auth/me", {
          headers: { Authorization: `Bearer ${storedJwt}` },
        })
        .then((res) => {
          applyAuthSession(res.data);
        })
        .catch((err) => {
          console.warn("Could not refresh the stored user profile:", err);
        })
        .finally(() => setReady(true));
    }
  }, []);

  return ready ? <>{children}</> : null;
}
